/**
 * Analytics stat access API paths.
 *
 * Staff / direct target: `/authenticated/{type}/{accountId}/analytics/stats/access/...`
 * Parent_delegate: `/authenticated/client/{parentId}/analytics/children/{childId}/stats/access/...`
 */

/** Staff or direct-target account (session account matches `:accountId`, or staff editing it). */
export function statAccessBasePath(accountType: string, accountId: string): string {
  return `/authenticated/${accountType}/${accountId}/analytics/stats/access`;
}

/** Parent managing a child — parent id must match the active session account. */
export function parentChildStatAccessBasePath(
  parentAccountId: string | number,
  childAccountId: string | number,
): string {
  return `/authenticated/client/${parentAccountId}/analytics/children/${childAccountId}/stats/access`;
}

export type StatAccessPathArgs = {
  accountType: string;
  /** Target account: the account whose limits are read/written (child when parent_delegate). */
  accountId: string;
  /**
   * When set, use the parent → children mount (parent_delegate).
   * `accountId` is the child; this is the parent client id in the path.
   */
  parentAccountId?: string | number | null;
};

export function resolveStatAccessBasePath({
  accountType,
  accountId,
  parentAccountId,
}: StatAccessPathArgs): string {
  if (parentAccountId != null && String(parentAccountId) !== '') {
    return parentChildStatAccessBasePath(parentAccountId, accountId);
  }
  return statAccessBasePath(accountType, accountId);
}

export function statAccessListPath(
  accountType: string,
  accountId: string,
  parentAccountId?: string | number | null,
): string {
  return resolveStatAccessBasePath({ accountType, accountId, parentAccountId });
}

export function statAccessColumnCatalogPath(
  accountType: string,
  accountId: string,
  parentAccountId?: string | number | null,
): string {
  return `${resolveStatAccessBasePath({ accountType, accountId, parentAccountId })}/column-catalog`;
}

export function statAccessLimitPath(
  accountType: string,
  accountId: string,
  statKey: string,
  parentAccountId?: string | number | null,
): string {
  return `${resolveStatAccessBasePath({
    accountType,
    accountId,
    parentAccountId,
  })}/${encodeURIComponent(statKey)}/limit`;
}

/** Parent_delegate only — parent + child both in path. */
export function statAccessInitializePath(
  parentAccountId: string | number,
  childAccountId: string | number,
): string {
  return `${parentChildStatAccessBasePath(parentAccountId, childAccountId)}/initialize`;
}

export function analyticsReportingModePath(accountType: string, accountId: string): string {
  return `/authenticated/${accountType}/${accountId}/analytics/stats/reporting-mode`;
}
